'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useCart } from '../contexts/CartContext';

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:4000';

type Collection = {
  id: string;
  title: string;
  handle: string;
  description: string | null;
  bannerImageUrl: string | null;
  featuredOnHomepage: boolean;
  sortOrder: number;
  /** Shoppable (in-stock, grouped) products at the selected location. */
  productCount: number;
};

export default function CollectionsList({ featuredOnly = false }: { featuredOnly?: boolean }) {
  const [collections, setCollections] = useState<Collection[]>([]);
  const [loading, setLoading] = useState(true);
  const { state } = useCart();

  useEffect(() => {
    loadCollections();
  }, [featuredOnly, state.storeCovaLocationId, state.fulfillmentMode]);

  const loadCollections = async () => {
    setLoading(true);
    try {
      // Build query params for location-aware collection filtering
      const params = new URLSearchParams();
      if (featuredOnly) {
        params.set('featured', 'true');
      }
      if (state.fulfillmentMode === 'pickup' && state.storeCovaLocationId) {
        // For pickup, use the specific store location ID
        params.set('covaLocationId', String(state.storeCovaLocationId));
        params.set('locationType', 'store');
      } else if (state.fulfillmentMode === 'delivery') {
        // For delivery, use same-day delivery type
        params.set('locationType', 'same-day');
        // Also include store ID if available (for location-specific collections)
        if (state.storeCovaLocationId) {
          params.set('covaLocationId', String(state.storeCovaLocationId));
        }
      }

      const url = `${API_BASE}/api/collections/public${params.toString() ? `?${params.toString()}` : ''}`;
      const res = await fetch(url);
      const data = await res.json();
      if (data?.success) {
        // Empty collections are dead ends for shoppers; keep them admin-only.
        setCollections(
          (data.collections || []).filter(
            (collection: Collection) => (collection.productCount ?? 0) > 0
          )
        );
      }
    } catch (err) {
      console.error('Failed to load collections:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-neutral-20 animate-pulse rounded-lg h-48" />
        ))}
      </div>
    );
  }

  if (collections.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {collections.map((collection) => (
        <Link
          key={collection.id}
          href={`/collections/${collection.handle}`}
          className="group relative overflow-hidden rounded-lg border border-neutral-20 transition-shadow"
        >
          {collection.bannerImageUrl ? (
            <div className="relative h-48 w-full overflow-hidden">
              <img
                src={collection.bannerImageUrl}
                alt={collection.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            </div>
          ) : (
            <div className="h-48 w-full bg-gradient-to-br from-emerald-100 to-slate-100 flex items-center justify-center">
              <div className="text-h1-mobile md:text-h2-desktop">📦</div>
            </div>
          )}
          <div className="p-4">
            <h3 className="font-semibold text-lg text-text-primary mb-1 group- transition-colors">
              {collection.title}
            </h3>
            {collection.description && (
              <p className="text-sm text-text-secondary line-clamp-2 mb-2">
                {collection.description}
              </p>
            )}
            <p className="text-xs text-text-tertiary">
              {collection.productCount} {collection.productCount === 1 ? 'product' : 'products'}
            </p>
          </div>
        </Link>
      ))}
    </div>
  );
}

